import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Factory, FlaskConical, Plus, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { Heading } from '../layout';
import { SettingsSplit, TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { ColorPaletteField } from '../forms/ColorPaletteField';
import { useUIColors } from '../../hooks/useTheme';
import {
  CATALOG_DEFINITIONS,
  CATALOG_GROUPS,
  DEFAULT_CATALOG_ITEMS,
  codeFromLabel,
  loadProductionCatalogItemsFromStorage,
  saveProductionCatalogItemsToStorage,
} from './productionCatalogDefaults';

const GROUP_ICONS = {
  requests: ClipboardList,
  shop: Factory,
  formulas: FlaskConical,
};

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  sort_order: 10,
  color_hex: '#2563eb',
  is_active: 'Sí',
};

const MinimalCatalogTable = ({
  rows = [],
  emptyLabel = 'Sin parámetros en este catálogo',
  onAdd,
  onEdit,
  onDelete,
}) => {
  const ui = useUIColors();
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
          String(a.label).localeCompare(String(b.label), 'es')
      ),
    [rows]
  );

  const allSelected = sortedRows.length > 0 && selectedIds.size === sortedRows.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sortedRows.map((row) => row.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    const selected = sortedRows.filter((row) => selectedIds.has(row.id));
    onDelete?.(selected);
  };

  const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
  };

  const tdStyle = {
    padding: '9px 10px',
    fontSize: '13px',
    color: ui.text,
    borderBottom: `1px solid color-mix(in srgb, ${ui.border} 70%, transparent)`,
    verticalAlign: 'middle',
  };

  const iconBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: ui.muted,
    cursor: 'pointer',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        {selectedIds.size > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 30,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${ui.border}`,
              background: ui.panel,
              color: '#b91c1c',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={14} /> Eliminar ({selectedIds.size})
          </button>
        ) : null}
        <button
          type="button"
          onClick={onAdd}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 30,
            padding: '0 10px',
            borderRadius: 6,
            border: 'none',
            background: ui.interactive,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Plus size={14} /> Agregar
        </button>
      </div>
      <div style={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr style={{ background: ui.header }}>
              <th style={{ ...thStyle, width: 36 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              </th>
              <th style={thStyle}>Código</th>
              <th style={thStyle}>Etiqueta</th>
              <th style={thStyle}>Color</th>
              <th style={thStyle}>Orden</th>
              <th style={thStyle}>Activo</th>
              <th style={{ ...thStyle, width: 72 }} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ ...tdStyle, color: ui.muted, textAlign: 'center', padding: 24 }}>
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.id}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{row.code}</td>
                  <td style={tdStyle}>{row.label}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        color: ui.muted,
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 999,
                          background: row.color_hex || '#64748b',
                          border: `1px solid ${ui.border}`,
                        }}
                      />
                      {row.color_hex || '—'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>{row.sort_order}</td>
                  <td style={tdStyle}>{row.is_active || 'Sí'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'inline-flex', gap: 2 }}>
                      <button type="button" title="Editar" onClick={() => onEdit?.(row)} style={iconBtn}>
                        <Pencil size={14} />
                      </button>
                      <button type="button" title="Eliminar" onClick={() => onDelete?.([row])} style={iconBtn}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GroupCatalogPanel = ({
  groupKey,
  selectedCatalogByGroup,
  setSelectedCatalogByGroup,
  catalogItemsByKey,
  openAdd,
  openEdit,
  handleDelete,
}) => {
  const ui = useUIColors();
  const definitions = CATALOG_DEFINITIONS.filter((item) => item.group === groupKey);
  const selectedCatalogKey = selectedCatalogByGroup[groupKey] || definitions[0]?.key;
  const selectedDefinition = CATALOG_DEFINITIONS.find((item) => item.key === selectedCatalogKey);
  const rows = catalogItemsByKey[selectedCatalogKey] || [];

  return (
    <SettingsSplit
      ariaLabel="Catálogos de producción"
      items={definitions.map((definition) => ({ key: definition.key, label: definition.label }))}
      activeKey={selectedCatalogKey}
      onChange={(key) =>
        setSelectedCatalogByGroup((prev) => ({
          ...prev,
          [groupKey]: key,
        }))
      }
    >
      <div style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>
          {selectedDefinition?.label || 'Catálogo'}
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted, lineHeight: 1.4 }}>
          {selectedDefinition?.description || ''}
        </p>
      </div>
      <MinimalCatalogTable
        rows={rows}
        onAdd={() => openAdd(selectedCatalogKey)}
        onEdit={(row) => openEdit(selectedCatalogKey, row)}
        onDelete={(payload) => handleDelete(selectedCatalogKey, payload)}
      />
    </SettingsSplit>
  );
};

const PaintProductionConfigWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [catalogItemsByKey, setCatalogItemsByKey] = useState(() => loadProductionCatalogItemsFromStorage());
  const [selectedCatalogByGroup, setSelectedCatalogByGroup] = useState({
    requests: 'product_request_status',
    shop: 'batch_status',
    formulas: 'formula_status',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCatalogKey, setEditingCatalogKey] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    saveProductionCatalogItemsToStorage(catalogItemsByKey);
  }, [catalogItemsByKey]);

  const editingDefinition = CATALOG_DEFINITIONS.find((item) => item.key === editingCatalogKey);

  const openAdd = (catalogKey) => {
    const current = catalogItemsByKey[catalogKey] || [];
    const nextOrder =
      current.length > 0 ? Math.max(...current.map((row) => Number(row.sort_order || 0))) + 10 : 10;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(null);
    setFormError('');
    setFormData({ ...EMPTY_FORM, sort_order: nextOrder });
    setShowForm(true);
  };

  const openEdit = (catalogKey, rowToEdit) => {
    if (!rowToEdit) return;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(rowToEdit.id);
    setFormError('');
    setFormData({
      code: rowToEdit.code || '',
      label: rowToEdit.label || '',
      description: rowToEdit.description || '',
      sort_order: Number(rowToEdit.sort_order || 10),
      color_hex: rowToEdit.color_hex || '#2563eb',
      is_active: rowToEdit.is_active || 'Sí',
    });
    setShowForm(true);
  };

  const handleDelete = (catalogKey, payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    const idsToDelete = new Set(rowsToDelete.map((row) => row?.id).filter(Boolean));
    if (!idsToDelete.size) return;
    setCatalogItemsByKey((prev) => ({
      ...prev,
      [catalogKey]: (prev[catalogKey] || []).filter((row) => !idsToDelete.has(row.id)),
    }));
  };

  const handleResetDefaults = () => {
    const ok = window.confirm(
      '¿Restablecer todos los catálogos de producción a los valores por defecto? Se perderán cambios locales.'
    );
    if (!ok) return;
    setCatalogItemsByKey({ ...DEFAULT_CATALOG_ITEMS });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingItemId(null);
    setEditingCatalogKey('');
    setFormData({ ...EMPTY_FORM });
  };

  const handleSave = () => {
    const label = String(formData.label || '').trim();
    let code = String(formData.code || '').trim();
    if (!label) {
      setFormError('La etiqueta es obligatoria.');
      return;
    }
    if (!code) code = codeFromLabel(label);
    if (!code) {
      setFormError('Código y etiqueta son obligatorios.');
      return;
    }
    const catalogKey = editingCatalogKey;
    if (!catalogKey) return;

    setCatalogItemsByKey((prev) => {
      const current = prev[catalogKey] || [];
      const nextRow = {
        code,
        label,
        description: String(formData.description || '').trim(),
        sort_order: Number(formData.sort_order || 0),
        color_hex: formData.color_hex || '#2563eb',
        is_active: formData.is_active || 'Sí',
      };
      if (editingItemId) {
        return {
          ...prev,
          [catalogKey]: current.map((row) =>
            row.id === editingItemId ? { ...row, ...nextRow } : row
          ),
        };
      }
      const nextId =
        current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
      return {
        ...prev,
        [catalogKey]: [...current, { id: nextId, ...nextRow }],
      };
    });
    closeForm();
  };

  const formSections = [
    {
      key: 'basic',
      title: 'Parámetro',
      fields: [
        { key: 'code', label: 'Código' },
        { key: 'label', label: 'Etiqueta' },
        { key: 'description', label: 'Descripción', fullWidth: true },
        { key: 'sort_order', label: 'Orden', type: 'number' },
        {
          key: 'color_hex',
          label: 'Color',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange, colors }) => (
            <ColorPaletteField
              value={value}
              onChange={onChange}
              label="Color"
              muted={colors.muted}
              text={colors.text}
              border={colors.inputBorder || colors.sectionBorder}
            />
          ),
        },
        {
          key: 'is_active',
          label: 'Activo',
          type: 'select',
          options: [
            { value: 'Sí', label: 'Sí' },
            { value: 'No', label: 'No' },
          ],
        },
      ],
    },
  ];

  const tabs = CATALOG_GROUPS.map((group) => {
    const Icon = GROUP_ICONS[group.key] || Factory;
    return {
      key: group.key,
      label: group.label,
      icon: <Icon size={16} />,
      content: (
        <GroupCatalogPanel
          groupKey={group.key}
          selectedCatalogByGroup={selectedCatalogByGroup}
          setSelectedCatalogByGroup={setSelectedCatalogByGroup}
          catalogItemsByKey={catalogItemsByKey}
          openAdd={openAdd}
          openEdit={openEdit}
          handleDelete={handleDelete}
        />
      ),
    };
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <div>
          <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: '6px' }}>
            Ajustes Producción
          </Heading>
          <p style={{ margin: 0, maxWidth: 640, fontSize: '13px', color: ui.muted, lineHeight: 1.45 }}>
            Define estados, orígenes y colores usados en Planificación, Producción (lotes) y Fórmulas.
            Los cambios se aplican de inmediato en esas pantallas.
          </p>
        </div>
        <button
          type="button"
          onClick={handleResetDefaults}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: '30px',
            padding: '0 10px',
            borderRadius: '6px',
            border: 'none',
            background: 'transparent',
            color: ui.muted,
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <RotateCcw size={14} />
          Restablecer defaults
        </button>
      </div>

      <TabNavigation tabs={tabs} defaultTab={CATALOG_GROUPS[0]?.key || 'requests'} isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={showForm}
        onClose={closeForm}
        onSave={handleSave}
        isDarkMode={isDarkMode}
        title={editingItemId ? 'Editar parámetro' : 'Nuevo parámetro'}
        subtitle={editingDefinition?.label || 'Catálogo'}
        sections={formSections}
        formData={formData}
        onChange={(key, value) => {
          setFormData((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'label' && !editingItemId && !String(prev.code || '').trim()) {
              next.code = codeFromLabel(value);
            }
            return next;
          });
        }}
        formError={formError}
        width={540}
      />
    </div>
  );
};

export default PaintProductionConfigWorkspace;
